// =============================================================================
// WebSocket singleton client — connect ke /ws di api-server.
// Pakai event-bus pattern: komponen subscribe ke type tertentu via
// `useWebSocketEvent(type, handler)` hook (lihat src/hooks/use-websocket.ts).
//
// Endpoint: /ws (modern). Auth: ?token=<JWT> (WAJIB, di-verify backend) +
// ?id_user= opsional (impersonasi admin/reseller).
// JANGAN pakai /websocket — itu legacy untuk app mobile aero lama.
//
// RESILIENCE (api-server down/up scenario):
//   - Single-flight connect: idempoten, tidak pernah buka koneksi ganda untuk
//     user yang sama (guard readyState CONNECTING/OPEN + detach handler socket
//     lama supaya event basi tidak mengganggu socket baru).
//   - Exponential backoff reconnect: 2s → 4s → 8s → ... capped 30s.
//   - Keepalive watchdog: deteksi "silent death" (server mati / network drop
//     tanpa frame close). Saat sepi, kirim app-ping {type:"ping"} (server balas
//     {type:"pong"}). Kalau tidak ada lalu lintas sama sekali dalam
//     ACTIVITY_TIMEOUT → paksa reconnect.
//   - online / visibilitychange → reconnect cepat (reset backoff) saat network
//     balik atau tab kembali fokus (mis. laptop bangun dari sleep).
//   - Status broadcast (`connecting|open|closed|reconnecting`) — UI bisa
//     subscribe via `ws.onStatusChange()` untuk tampilkan badge/toast.
//   - Saat reconnect sukses SETELAH pernah disconnect, trigger callback
//     `onReconnected()` supaya caller bisa re-fetch initial state (mis.
//     vehicles list yang mungkin missed update saat WS putus).
// =============================================================================

import { useAuthStore } from '@/store/auth-store';

export type WsMessage = {
  type: string;
  [key: string]: unknown;
};

export type WsStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

type WsHandler = (msg: WsMessage) => void;
type StatusHandler = (status: WsStatus) => void;
type ReconnectHandler = () => void;

class WsClient {
  private socket: WebSocket | null = null;
  private listeners = new Map<string, Set<WsHandler>>();
  private statusListeners = new Set<StatusHandler>();
  private reconnectListeners = new Set<ReconnectHandler>();
  private reconnectTimer: number | null = null;
  private keepaliveTimer: number | null = null;
  private currentUserId: number | null = null;
  private intentionalClose = false;
  private status: WsStatus = 'idle';
  // Reconnect attempt counter — di-reset ke 0 saat connection sukses,
  // increment saat close. Backoff = min(2^N * 1000, 30_000).
  private attempt = 0;
  // True kalau pernah open dan kemudian putus (untuk trigger onReconnected
  // saat next open, supaya caller bisa re-fetch state yang missed).
  private wasDisconnected = false;
  // Timestamp (ms) frame terakhir yang diterima — basis watchdog liveness.
  private lastActivityAt = 0;

  // Keepalive tuning. Server kirim WS-ping tiap ~81s & expect pong dalam 90s;
  // di sisi client kita pakai app-ping saat sepi + watchdog timeout.
  private readonly PING_INTERVAL = 30_000;     // ping kalau sepi >= 30s
  private readonly ACTIVITY_TIMEOUT = 70_000;  // no traffic 70s → anggap mati
  private readonly KEEPALIVE_TICK = 15_000;    // interval cek watchdog

  constructor() {
    // Recovery cepat saat network balik / tab kembali fokus. Guard SSR.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.kickReconnect());
      window.addEventListener('focus', () => this.kickReconnect());
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.kickReconnect();
      });
    }
  }

  private setStatus(s: WsStatus) {
    if (this.status === s) return;
    this.status = s;
    this.statusListeners.forEach((fn) => { try { fn(s); } catch { /* swallow */ } });
  }

  /** Current status snapshot (untuk inisialisasi UI tanpa nunggu event). */
  getStatus(): WsStatus { return this.status; }

  /**
   * Subscribe ke perubahan status koneksi (idle/connecting/open/reconnecting/
   * closed). Return unsubscribe.
   */
  onStatusChange(fn: StatusHandler): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  /**
   * Subscribe ke event "reconnected setelah putus". Dipakai caller untuk
   * re-fetch initial state (mis. queryClient.invalidate(['vehicles'])).
   */
  onReconnected(fn: ReconnectHandler): () => void {
    this.reconnectListeners.add(fn);
    return () => this.reconnectListeners.delete(fn);
  }

  /**
   * connect: buka koneksi WS dengan user_id sebagai query param.
   * Idempoten — kalau sudah ada socket hidup (CONNECTING/OPEN) untuk user yang
   * sama, no-op (cegah koneksi ganda saat StrictMode/HMR/re-render).
   * Auto-reconnect dengan exponential backoff kalau close tidak sengaja.
   */
  connect(userId: number) {
    if (
      this.socket &&
      this.currentUserId === userId &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return; // sudah ada koneksi hidup ke user yang sama — jangan dobel
    }
    // Tutup socket lama (user beda / state mati) tanpa men-trigger reconnect.
    this.teardownSocket();
    this.currentUserId = userId;
    this.intentionalClose = false;
    this.setStatus(this.attempt > 0 ? 'reconnecting' : 'connecting');

    // Backend /ws WAJIB ?token=<JWT> (di-verify). id_user opsional → impersonasi
    // (admin/reseller lihat user lain; backend authorize via canViewUser).
    const token = useAuthStore.getState().token ?? '';
    const url = `${this.baseUrl()}/ws?token=${encodeURIComponent(token)}&id_user=${userId}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return; // event dari socket basi — abaikan
      console.info('[ws] connected user_id=' + userId);
      this.attempt = 0;
      this.lastActivityAt = Date.now();
      this.setStatus('open');
      this.startKeepalive();
      // Fire onReconnected hanya kalau ini bukan koneksi pertama (yaitu
      // pernah putus dan recover). Caller dapat sinyal untuk re-sync state.
      if (this.wasDisconnected) {
        this.wasDisconnected = false;
        this.reconnectListeners.forEach((fn) => { try { fn(); } catch { /* swallow */ } });
      }
    };

    socket.onmessage = (e) => {
      if (this.socket !== socket) return;
      this.lastActivityAt = Date.now();
      try {
        const msg = JSON.parse(e.data) as WsMessage;
        // 'pong' cuma untuk liveness — sudah tercatat di lastActivityAt, skip dispatch.
        if (msg.type === 'pong') {
          console.debug('[ws] recv pong');
          return;
        }
        // Log SEMUA data masuk WS — sekali per pesan, SEBELUM dispatch, jadi
        // pesan tanpa listener pun terlihat (debugging end-to-end).
        console.log('[ws] recv', msg.type, msg);
        const handlers = this.listeners.get(msg.type);
        if (!handlers || handlers.size === 0) {
          console.debug('[ws] tidak ada listener untuk type', msg.type);
        }
        handlers?.forEach((fn) => {
          try { fn(msg); } catch (err) { console.error('[ws] handler error', err); }
        });
      } catch (err) {
        console.error('[ws] parse error', err, e.data);
      }
    };

    socket.onclose = (e) => {
      if (this.socket !== socket) return; // close socket basi — abaikan
      console.info('[ws] closed code=' + e.code);
      this.socket = null;
      this.stopKeepalive();
      if (this.intentionalClose) {
        this.setStatus('closed');
        return;
      }
      // Putus tak sengaja → next open harus re-sync state.
      this.wasDisconnected = true;
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // Biarkan onclose yang meng-handle reconnect (error selalu diikuti close).
      if (this.socket !== socket) return;
      console.warn('[ws] socket error');
    };
  }

  /**
   * disconnect: tutup koneksi tanpa reconnect.
   * Dipanggil saat logout atau saat switch user (sebelum connect baru).
   */
  disconnect() {
    this.intentionalClose = true;
    this.teardownSocket();
    this.setStatus('closed');
  }

  /**
   * reconnect: shorthand untuk switch user (mis. impersonation berubah).
   */
  reconnect(userId: number) {
    this.disconnect();
    this.connect(userId);
  }

  /**
   * send: kirim payload ke server (mis. sendcmd). Drop kalau belum open.
   */
  send(payload: object): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('[ws] send dropped — socket not open');
      return false;
    }
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  /**
   * on: subscribe ke message dengan type tertentu.
   * Return unsubscribe function. Selalu pakai via `useWebSocketEvent` hook
   * di React component untuk auto-cleanup.
   */
  on(type: string, handler: WsHandler): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(handler);
    return () => {
      set!.delete(handler);
      if (set!.size === 0) this.listeners.delete(type);
    };
  }

  // --- internal helpers -----------------------------------------------------

  // teardownSocket: detach handler + close socket sekarang TANPA menjadwalkan
  // reconnect. Detach penting supaya onclose socket lama tidak nyangkut dan
  // memicu reconnect/null-ing socket baru (race saat re-connect cepat).
  private teardownSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopKeepalive();
    const s = this.socket;
    this.socket = null;
    if (s) {
      s.onopen = null;
      s.onmessage = null;
      s.onclose = null;
      s.onerror = null;
      try { s.close(1000, 'manual'); } catch { /* swallow */ }
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return; // sudah ada yang dijadwalkan
    if (this.currentUserId === null) return;
    // Exponential backoff: 2s, 4s, 8s, 16s, 30s (capped).
    const delay = Math.min(30_000, 2000 * Math.pow(2, this.attempt));
    this.attempt += 1;
    console.info('[ws] reconnect dalam', delay, 'ms (attempt', this.attempt, ')');
    this.setStatus('reconnecting');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.currentUserId !== null && !this.intentionalClose) {
        this.connect(this.currentUserId);
      }
    }, delay);
  }

  // kickReconnect: dipanggil dari online/focus/visibility. Reconnect segera
  // (reset backoff) kalau socket tidak hidup. No-op kalau sengaja ditutup.
  private kickReconnect() {
    if (this.intentionalClose || this.currentUserId === null) return;
    const alive = this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN);
    if (alive) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempt = 0; // recovery cepat — jangan tunggu backoff lama
    this.connect(this.currentUserId);
  }

  // startKeepalive: satu timer yang (a) kirim app-ping saat sepi, dan
  // (b) paksa reconnect kalau benar-benar tidak ada lalu lintas (silent death).
  // Akun aktif (banyak frame posisi) tidak pernah ping → tidak nambah log server.
  private startKeepalive() {
    this.stopKeepalive();
    this.keepaliveTimer = window.setInterval(() => {
      const s = this.socket;
      if (!s || s.readyState !== WebSocket.OPEN) return;
      const idle = Date.now() - this.lastActivityAt;
      if (idle >= this.ACTIVITY_TIMEOUT) {
        console.warn('[ws] no activity', idle, 'ms — forcing reconnect');
        this.forceReconnect();
        return;
      }
      if (idle >= this.PING_INTERVAL) {
        try { s.send(JSON.stringify({ type: 'ping' })); } catch { /* swallow */ }
      }
    }, this.KEEPALIVE_TICK);
  }

  private stopKeepalive() {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  // forceReconnect: tutup socket "zombie" (open tapi mati) lalu connect ulang.
  private forceReconnect() {
    const uid = this.currentUserId;
    this.teardownSocket();
    this.wasDisconnected = true;
    this.attempt = 0;
    if (uid !== null) this.connect(uid);
  }

  private baseUrl(): string {
    const envBase = import.meta.env.VITE_WS_BASE;
    if (envBase) return envBase;
    // Derive dari window.location: http → ws, https → wss
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}`;
  }
}

export const ws = new WsClient();
